<template>
  <div id="liquid-level-vessel-info">
    <vs-collapse id="contents">
      <vs-collapse-item :open="initiallyOpened">
        <vs-row slot="header" class="content-row" style="flex flex-wrap">
          <template v-for="(column, index) in columns">
            <vs-col
              v-if="column.key == 'vesselName'"
              v-bind:id="`vessel-name-${vesselId}`"
              class="cell vessel-name-cell"
              :class="generateClasses(column)"
              :key="index"
              vs-type="flex"
              vs-align="start"
              vs-lg="2"
              vs-sm="9"
              vs-xs="7"
              vs-order="1"
            >
              <p
                v-bind:id="`vessel-name-value-${vesselId}`"
                class="aisea-text-14-400-black"
                style="white-space: hidden; overflow: hidden; text-overflow: ellipsis;"
              >
                {{ vesselName }}
              </p>
            </vs-col>
            <vs-col
              v-else-if="column.key == 'draft'"
              v-bind:id="`draft-${vesselId}`"
              class="cell draft-cell"
              :class="generateClasses(column)"
              :key="column.key"
              vs-type="flex"
              vs-align="start"
              vs-lg="3"
              vs-sm="5"
              vs-xs="5"
              vs-order="2"
            >
              <vs-col
                v-bind:id="`bow-draft-${vesselId}`"
                class="bow-draft"
                vs-lg="6"
                vs-sm="6"
                vs-xs="12"
              >
                <div class="flex">
                  <p
                    v-bind:id="`bow-draft-text-${vesselId}`"
                    class="flex items-center aisea-text-14-400-black"
                    style="margin-right: 5px"
                  >
                    船首喫水
                  </p>
                  <p
                    v-bind:id="`bow-draft-value-${vesselId}`"
                    class="aisea-text-14-400-black"
                    :style="{ color: draftFontColor }"
                  >
                    {{ draft.bowDraft }}m
                  </p>
                </div>
              </vs-col>
              <vs-col
                v-bind:id="`stern-draft-${vesselId}`"
                class="stern-draft"
                vs-lg="6"
                vs-sm="6"
                vs-xs="12"
              >
                <div class="flex">
                  <p
                    v-bind:id="`stern-draft-text-${vesselId}`"
                    class="flex items-center aisea-text-14-400-black"
                    style="margin-right: 5px"
                  >
                    船尾喫水
                  </p>
                  <p
                    v-bind:id="`stern-draft-value-${vesselId}`"
                    class="aisea-text-14-400-black"
                    :style="{ color: draftFontColor }"
                  >
                    {{ draft.sternDraft }}m
                  </p>
                </div>
              </vs-col>
            </vs-col>
            <vs-col
              v-else-if="column.key == 'tilt'"
              v-bind:id="`tilt-${vesselId}`"
              class="cell tilt-cell"
              :class="generateClasses(column)"
              :key="column.key"
              vs-type="flex"
              vs-align="start"
              vs-lg="5"
              vs-sm="5"
              vs-xs="6"
              vs-order="3"
            >
              <vs-col
                v-bind:id="`trim-${vesselId}`"
                class="trim-stern"
                vs-lg="4"
                vs-sm="6"
                vs-xs="12"
              >
                <div class="flex">
                  <p
                    v-bind:id="`trim-text-${vesselId}`"
                    class="flex items-center aisea-text-14-400-black"
                    style="margin-right: 5px"
                  >
                    トリム
                  </p>
                  <p
                    v-bind:id="`trim-board-${vesselId}`"
                    class="flex items-center aisea-text-14-400-black"
                    style="margin-right: 5px"
                    :style="{ color: trimAngleFontColor }"
                  >
                    {{ tilt.trimBoard }}
                  </p>
                  <p
                    v-bind:id="`trim-value-${vesselId}`"
                    class="aisea-text-14-400-black"
                    :style="{ color: trimValueFontColor }"
                  >
                    <span v-if="tilt.trim !== null">{{ Math.abs(tilt.trim / 10).toFixed(1) }}m</span>
                  </p>
                </div>
              </vs-col>
              <vs-col
                v-bind:id="`trim-image-area-${vesselId}`"
                class="trim-image-area"
                vs-type="flex"
                vs-lg="3"
                vs-sm="5"
                vs-xs="6"
              >
                <img
                  v-bind:id="`trim-image-${vesselId}`"
                  class="trim-image"
                  src="@/assets/images/eco-ship/vessel_image.svg"
                  :style="{
                    transform: 'rotate(' + (tilt.trim/10).toFixed(1) + 'deg)'
                  }"
                />
                <div v-bind:id="`reference-trim-line-${vesselId}`" class="reference-trim-line"></div>
              </vs-col>
              <vs-col v-bind:id="`heel-${vesselId}`" class="heel" vs-lg="3" vs-sm="6" vs-xs="12">
                <div class="flex">
                  <p
                    v-bind:id="`heel-text-${vesselId}`"
                    class="aisea-text-14-400-black flex items-center"
                    style="margin-right: 5px"
                  >
                    ヒール
                  </p>
                  <p
                    v-bind:id="`heel-board-${vesselId}`"
                    class="flex items-center aisea-text-14-400-black"
                    style="margin-right: 5px"
                    :style="{ color: heelAngleFontColor }"
                  >
                    {{ tilt.heelBoard }}
                  </p>
                  <p
                    v-bind:id="`heel-value-${vesselId}`"
                    class="aisea-text-14-400-black"
                    :style="{ color: heelValueFontColor }"
                  >
                    <span v-if="tilt.heel !== null">{{ Math.abs(tilt.heel / 10).toFixed(1) }}°</span>
                  </p>
                </div>
              </vs-col>
              <vs-col
                v-bind:id="`heel-image-area-${vesselId}`"
                class="heel-image-area"
                vs-type="flex"
                vs-align="start"
                vs-lg="2"
                vs-sm="5"
                vs-xs="6"
              >
                <img
                  v-bind:id="`heel-image-${vesselId}`"
                  class="heel-image"
                  src="@/assets/images/eco-ship/heel_image.svg"
                  :style="{
                    transform: 'rotate(' + (tilt.heel/10).toFixed(1) + 'deg)'
                  }"
                />
                <div v-bind:id="`reference-heel-line-${vesselId}`" class="reference-heel-line"></div>
              </vs-col>
            </vs-col>
            <vs-col
              v-else-if="column.key == 'lastUpdatedAt'"
              v-bind:id="`last-update-${vesselId}`"
              class="cell data-reception"
              :class="generateClasses(column)"
              :key="column.key"
              vs-type="flex"
              vs-align="start"
              vs-lg="2"
              vs-sm="3"
              vs-xs="5"
              vs-order="4"
            >
              <p v-if="levelGaugeLastUpdatedAt === -1" v-bind:id="`date-valeu-${vesselId}`" class="aisea-text-12-400-black">
                -
              </p>
              <p v-else v-bind:id="`date-valeu-${vesselId}`" class="aisea-text-12-400-black">
                {{ lastUpdatedAtYmd }}
              </p>
            </vs-col>
          </template>
        </vs-row>
        <div v-bind:id="`tanks-contents-${vesselId}`" class="tanks">
          <div class="fuel-oil">
            <fuel-oil
              :vesselId="vesselId"
              :data="levelGauge"
              :fontColor="tanksFontColor"
            ></fuel-oil>
          </div>
          <div class="fresh-water">
            <fresh-water
              :vesselId="vesselId"
              :data="levelGauge"
              :fontColor="tanksFontColor"
            ></fresh-water>
          </div>
          <div class="ballast">
            <ballast
              :vesselId="vesselId"
              :data="levelGauge"
              :fontColor="tanksFontColor"
            ></ballast>
          </div>
          <div class="lost">
            <lost
              :vesselId="vesselId"
              :data="lostData"
            ></lost>
          </div>
        </div>
        <vs-row style="margin-top: 1%">
          <vs-col v-bind:id="`abnormal-display-${vesselId}`" vs-lg="3" vs-sm="4" vs-xs="12">
            <vs-col
              v-bind:id="`abnormal-display-text-${vesselId}`"
              class="aisea-text-14-700-dark-gray"
              vs-lg="4"
              vs-sm="4"
              vs-xs="3"
              >異常表示</vs-col
            >
            <vs-col v-bind:id="`plc-cell-${vesselId}`" vs-lg="4" vs-sm="4" vs-xs="3" vs-type="flex">
              <div
                class="circle-display"
                :style="{
                  backgroundColor: plcInnerColor,
                  borderColor: plcOuterColor
                }"
              ></div>
              <p class="aisea-text-14-400-black">PLC</p>
            </vs-col>
            <vs-col
              v-bind:id="`device-cell-${vesselId}`"
              vs-lg="4"
              vs-sm="4"
              vs-xs="3"
              vs-type="flex"
            >
              <div
                class="circle-display"
                :style="{
                  backgroundColor: deviceInnerColor,
                  borderColor: deviceOuterColor
                }"
              ></div>
              <p class="aisea-text-14-400-black">装置</p>
            </vs-col>
          </vs-col>
          <vs-col v-bind:id="`communication-error-${vesselId}`" vs-lg="4" vs-sm="5" vs-xs="12">
            <vs-col
              v-bind:id="`communication-error-text-${vesselId}`"
              class="aisea-text-14-700-dark-gray"
              vs-lg="3"
              vs-sm="3"
              vs-xs="3"
              >通信異常</vs-col
            >
            <vs-col v-bind:id="`tb5-cell-${vesselId}`" vs-lg="3" vs-sm="3" vs-xs="3" vs-type="flex">
              <div
                class="circle-display"
                :style="{
                  backgroundColor: tb5InnerColor,
                  borderColor: tb5OuterColor
                }"
              ></div>
              <p class="aisea-text-14-400-black">TB5</p>
            </vs-col>
            <vs-col v-bind:id="`tb6-cell-${vesselId}`" vs-lg="3" vs-sm="3" vs-xs="3" vs-type="flex">
              <div
                class="circle-display"
                :style="{
                  backgroundColor: tb6InnerColor,
                  borderColor: tb6OuterColor
                }"
              ></div>
              <p class="aisea-text-14-400-black">TB6</p>
            </vs-col>
            <vs-col
              v-bind:id="`tiltmeter-cell-${vesselId}`"
              vs-lg="3"
              vs-sm="3"
              vs-xs="3"
              vs-type="flex"
            >
              <div
                class="circle-display"
                :style="{
                  backgroundColor: tiltmeterInnerColor,
                  borderColor: tiltmeterOuterColor
                }"
              ></div>
              <p class="aisea-text-14-400-black">傾斜計</p>
            </vs-col>
          </vs-col>
        </vs-row>
      </vs-collapse-item>
    </vs-collapse>
  </div>
</template>

<script>
import moment from "moment";
import Ballast from "./components/Ballast.vue";
import FreshWater from "./components/FreshWater.vue";
import FuelOil from "./components/FuelOil.vue";
import Lost from "./components/Lost.vue";
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapState } = createNamespacedHelpers("ecoShip");
import { DeviceInfoMixin, ColorMixin } from "@/mixins";

export default {
  components: { Ballast, FreshWater, FuelOil, Lost },
  mixins: [DeviceInfoMixin, ColorMixin],
  props: {
    columns: { type: Array },
    vesselId: { type: String },
    vesselName: { type: String },
    initiallyOpened: { type: Boolean, default: false }
  },
  data() {
    return {
      draft: {
        bowDraft: "",
        sternDraft: ""
      },
      tilt: {
        trim: null,
        trimBoard: "",
        trimAngle: "",
        heel: null,
        heelBoard: "",
      },
      tanksFontColor: "",
      draftFontColor: "",
      trimAngleFontColor: "",
      trimValueFontColor: "",
      heelAngleFontColor: "",
      heelValueFontColor: "",
      plcInnerColor: "",
      plcOuterColor: "",
      deviceInnerColor: "",
      deviceOuterColor: "",
      tb5InnerColor: "",
      tb5OuterColor: "",
      tb6InnerColor: "",
      tb6OuterColor: "",
      tiltmeterInnerColor: "",
      tiltmeterOuterColor: "",
    };
  },
  computed: {
    ...mapState({
      versionId: state => state.kind.level_gauge.versionId,
      versionName: state => state.kind.level_gauge.versionName,
      levelGaugeDashboard: state => state.levelGaugeDashboard,
      lostData: state => state.lostData,
      levelGauge: state => state.levelGauge,
      levelGaugeLastUpdatedAt: state => state.levelGaugeLastUpdatedAt
    }),
    lastUpdatedAtYmd() {
      return moment(this.levelGaugeLastUpdatedAt)
        .local()
        .format("YYYY/MM/DD HH:mm");
    },
  },
  watch: {
    levelGaugeLastUpdatedAt() {
      this.setDraftAndTilt(this.levelGaugeDashboard, this.levelGauge);
    },
    onChangeWindowSize() {
      this.setContentSize();
    },
  },
  async created() {
    await this.fetchLevelGauge();
  },
  methods: {
    ...mapActions([
      "setVersionState",
      "fetchLevelGaugeDashboardAll",
      "fetchLevelGaugeDashboard",
      "getLostData",
      "getVesselChannelData",
      "unsubscribeFirestore"
    ]),
    async fetchLevelGauge() {
      let payload = {
        enterprise_id: this.$store.state.auth.user.id,
        vessel_id: this.vesselId,
        params: {
          kind: "level_gauge",
          origin: 3
        }
      };
      await this.setVersionState(payload);

      payload["name"] = this.versionName;
      await this.getVesselChannelData(payload);
      await this.getLostData(payload);

      let dashboard = await this.fetchLevelGaugeDashboard(payload);
      await this.setDraftAndTilt(dashboard, this.levelGauge);
    },
    async setDraftAndTilt(dashboardData, levelGaugeData) {

      if (levelGaugeData[this.vesselId] && (Object.keys(dashboardData).length)) {

        let data = levelGaugeData[this.vesselId];

        this.draft.bowDraft = data[dashboardData.itemList.data.bow_draught.channel_id].vn / 100;
        this.draft.sternDraft = data[dashboardData.itemList.data.stern_draught.channel_id].vn / 100;

        let trimAngle = data[dashboardData.trimList.data.draught_value.channel_id].vn / 10;
        if (data[dashboardData.trimList.data.tilt.channel_id].vn === 0) {
          this.tilt.trimBoard = "船首 ";
          this.tilt.trim = trimAngle;
        } else if (data[dashboardData.trimList.data.tilt.channel_id].vn === 1) {
          this.tilt.trimBoard = "船尾 ";
          this.tilt.trim = trimAngle;
        } else {
          this.tilt.trimBoard = "傾き無 ";
          this.tilt.trim = trimAngle;
        }

        if (data[dashboardData.heelList.data.tilt.channel_id].vn === 0) {
          this.tilt.heelBoard = "左舷 ";
          this.tilt.heel = data[dashboardData.heelList.data.draught_value.channel_id].vn;
        } else if (data[dashboardData.heelList.data.tilt.channel_id].vn === 1) {
          this.tilt.heelBoard = "右舷 ";
          this.tilt.heel = data[dashboardData.heelList.data.draught_value.channel_id].vn;
        } else {
          this.tilt.heelBoard = "傾き無 ";
          this.tilt.heel = data[dashboardData.heelList.data.draught_value.channel_id].vn;
        }
        await this.setCircleColor(data, dashboardData);
        await this.setFontColor(data, dashboardData);
      }
    },
    async setCircleColor(data, dashboardData) {
      if (data[dashboardData.itemList.data.plc.channel_id].vn === 0) {
        this.plcInnerColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.plcOuterColor = this.AISEA_COLOR.LIGHT_GRAY;
      } else {
        this.plcInnerColor = this.AISEA_COLOR.PINK;
        this.plcOuterColor = "#fcc0ce";
      }

      if (data[dashboardData.itemList.data.device.channel_id].vn === 0) {
        this.deviceInnerColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.deviceOuterColor = this.AISEA_COLOR.LIGHT_GRAY;
      } else {
        this.deviceInnerColor = this.AISEA_COLOR.PINK;
        this.deviceOuterColor = "#fcc0ce";
      }

      if (data[dashboardData.itemList.data.tb5.channel_id].vn === 0) {
        this.tb5InnerColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.tb5OuterColor = this.AISEA_COLOR.LIGHT_GRAY;
      } else {
        this.tb5InnerColor = this.AISEA_COLOR.PINK;
        this.tb5OuterColor = "#fcc0ce";
      }

      if (data[dashboardData.itemList.data.tb6.channel_id].vn === 0) {
        this.tb6InnerColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.tb6OuterColor = this.AISEA_COLOR.LIGHT_GRAY;
      } else {
        this.tb6InnerColor = this.AISEA_COLOR.PINK;
        this.tb6OuterColor = "#fcc0ce";
      }

      if (data[dashboardData.itemList.data.tiltmeter.channel_id].vn === 0) {
        this.tiltmeterInnerColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.tiltmeterOuterColor = this.AISEA_COLOR.LIGHT_GRAY;
      } else {
        this.tiltmeterInnerColor = this.AISEA_COLOR.PINK;
        this.tiltmeterOuterColor = "#fcc0ce";
      }
    },
    async setFontColor(data, dashboardData) {

      let tb5 = data[dashboardData.itemList.data.tb5.channel_id].vn;
      let trim = data[dashboardData.trimList.data.tilt.channel_id].vn;
      let  heel = data[dashboardData.heelList.data.tilt.channel_id].vn;

      if (tb5 === 0) {
        this.tanksFontColor = this.AISEA_COLOR.BLACK;
        this.draftFontColor = this.AISEA_COLOR.BLACK;
        this.trimValueFontColor = this.AISEA_COLOR.BLACK;
        this.heelValueFontColor = this.AISEA_COLOR.BLACK;
      } else {
        this.tanksFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.draftFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.trimValueFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
        this.heelValueFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
      }

      if (tb5 === 1 || trim === 2) {
        this.trimAngleFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
      } else {
        this.trimAngleFontColor = this.AISEA_COLOR.BLACK;
      }

      if (tb5 === 1 || heel === 2) {
        this.heelAngleFontColor = this.AISEA_COLOR.SLIGHTLY_GRAY;
      } else {
        this.heelAngleFontColor = this.AISEA_COLOR.BLACK;
      }
    },
    generateClasses(columnData) {
      let classObj = {};
      classObj[columnData.key] = true;
      if (Object.prototype.hasOwnProperty.call(columnData, "cellClass")){
        classObj[columnData.cellClasses] = true;
      }
      return classObj;
    },
    async setContentSize() {

      let deviceInfo = this.getDeviceInfo();

      const nameCell = document.getElementById("vessel-name-" + this.vesselId);
      const nameValue = document.getElementById("vessel-name-value-" + this.vesselId);
      const draftCell = document.getElementById("draft-" + this.vesselId);
      const bowDraft = document.getElementById("bow-draft-" + this.vesselId);
      const bowDraftValue = document.getElementById("bow-draft-value-" + this.vesselId);
      const sternDraft = document.getElementById("stern-draft-" + this.vesselId);
      const sternDraftValue = document.getElementById("stern-draft-value-" + this.vesselId);
      const tiltCell = document.getElementById("tilt-" + this.vesselId);
      const trim = document.getElementById("trim-" + this.vesselId);
      const tirmValue = document.getElementById("trim-value-" + this.vesselId);
      const trimCell = document.getElementById("trim-image-area-" + this.vesselId);
      const trimImage = document.getElementById("trim-image-" + this.vesselId);
      const referenceTrimLine = document.getElementById("reference-trim-line-" + this.vesselId);
      const heel = document.getElementById("heel-" + this.vesselId);
      const heelText = document.getElementById("heel-text-" + this.vesselId);
      const heelValue = document.getElementById("heel-value-" + this.vesselId);
      const heelCell = document.getElementById("heel-image-area-" + this.vesselId);
      const heelImage = document.getElementById("heel-image-" + this.vesselId);
      const referenceHeelLine = document.getElementById("reference-heel-line-" + this.vesselId);
      const dateCell = document.getElementById("last-update-" + this.vesselId);
      const tanks = document.getElementById("tanks-contents-" + this.vesselId);
      const communicationErrorCell = document.getElementById("communication-error-" + this.vesselId);
      const tb5Cell = document.getElementById("tb5-cell-" + this.vesselId);
      const tb6Cell = document.getElementById("tb6-cell-" + this.vesselId);
      const accordionButton = document.getElementsByClassName("icon-header");

      if ( deviceInfo.deviceType === "PC" && deviceInfo.width <= 1366 ) {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "start";
        draftCell.style.alignItems = "start";
        tiltCell.style.alignItems = "start";
        dateCell.style.alignItems = "start";

        sternDraft.style.marginTop = "5%";
        trimCell.style.left = "-19%";
        trimImage.style.left = "5%";
        referenceTrimLine.style.width = "60%";

        heel.style.marginTop = "5%";
        heelCell.style.marginTop = "5%";
        heelCell.style.left = "-21%";
        heelText.style.marginLeft = "";
        heelImage.style.left = "15%";
        referenceHeelLine.style.width = "50%";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-lg", "vs-lg-2");
        this.changeSize(draftCell, "vs-lg", "vs-lg-2");
        this.changeSize(bowDraft, "vs-lg", "vs-lg-12");
        this.changeSize(sternDraft, "vs-lg", "vs-lg-12");
        this.changeSize(tiltCell, "vs-lg", "vs-lg-5");
        this.changeSize(trim, "vs-lg", "vs-lg-5");
        this.changeSize(trimCell, "vs-lg", "vs-lg-5");
        this.changeSize(heel, "vs-lg", "vs-lg-5");
        this.changeSize(heelCell, "vs-lg", "vs-lg-5");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "17%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPad" && deviceInfo.deviceOrientation === "landscape" && 1024 < deviceInfo.width && deviceInfo.width <= 1366 ) {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "start";
        draftCell.style.alignItems = "start";
        tiltCell.style.alignItems = "start";
        dateCell.style.alignItems = "start";

        sternDraft.style.marginTop = "5%";
        trimCell.style.left = "-11%";
        trimImage.style.left = "-20%";
        referenceTrimLine.style.width = "40%";
        referenceTrimLine.style.left = "25%";
        heel.style.marginTop = "5%";
        heelCell.style.marginTop = "5%";
        heelCell.style.left = "-10%";
        heelText.style.marginLeft = "";
        heelImage.style.left = "-11%";
        referenceHeelLine.style.width = "40%";
        referenceHeelLine.style.left = "24%";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-lg", "vs-lg-2");
        this.changeSize(draftCell, "vs-lg", "vs-lg-3");
        this.changeSize(bowDraft, "vs-lg", "vs-lg-12");
        this.changeSize(sternDraft, "vs-lg", "vs-lg-12");
        this.changeSize(tiltCell, "vs-lg", "vs-lg-5");
        this.changeSize(trim, "vs-lg", "vs-lg-4");
        this.changeSize(trimCell, "vs-lg", "vs-lg-6");
        this.changeSize(heel, "vs-lg", "vs-lg-4");
        this.changeSize(heelCell, "vs-lg", "vs-lg-5");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "17%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPad" && deviceInfo.deviceOrientation === "portrait" && 980 < deviceInfo.width && deviceInfo.width <= 1083 ) {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "start";
        draftCell.style.alignItems = "start";
        tiltCell.style.alignItems = "start";
        dateCell.style.alignItems = "start";

        sternDraft.style.marginTop = "5%";
        trimCell.style.left = "-12%";
        trimImage.style.left = "5%";
        referenceTrimLine.style.width = "50%";
        referenceTrimLine.style.left = "";
        heel.style.marginTop = "5%";
        heelCell.style.marginTop = "5%";
        heelCell.style.left = "-10%";
        heelText.style.marginLeft = "";
        heelImage.style.left = "16%";
        referenceHeelLine.style.width = "50%";
        referenceHeelLine.style.left = "";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-lg", "vs-lg-2");
        this.changeSize(draftCell, "vs-lg", "vs-lg-3");
        this.changeSize(bowDraft, "vs-lg", "vs-lg-12");
        this.changeSize(sternDraft, "vs-lg", "vs-lg-12");
        this.changeSize(tiltCell, "vs-lg", "vs-lg-5");
        this.changeSize(trim, "vs-lg", "vs-lg-4");
        this.changeSize(trimCell, "vs-lg", "vs-lg-6");
        this.changeSize(heel, "vs-lg", "vs-lg-4");
        this.changeSize(heelCell, "vs-lg", "vs-lg-5");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "17%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPad" && deviceInfo.deviceOrientation === "landscape" && deviceInfo.width <= 1024 ) {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "start";
        draftCell.style.alignItems = "start";
        tiltCell.style.alignItems = "start";
        dateCell.style.alignItems = "start";

        sternDraft.style.marginTop = "5%";
        trimCell.style.left = "-13%";
        trimImage.style.left = "5%";
        referenceTrimLine.style.width = "50%";
        referenceTrimLine.style.left = "";
        heel.style.marginTop = "5%";
        heelCell.style.marginTop = "5%";
        heelCell.style.left = "-11%";
        heelText.style.marginLeft = "";
        heelImage.style.left = "16%";
        referenceHeelLine.style.width = "50%";
        referenceHeelLine.style.left = "";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-lg", "vs-lg-2");
        this.changeSize(draftCell, "vs-lg", "vs-lg-3");
        this.changeSize(bowDraft, "vs-lg", "vs-lg-12");
        this.changeSize(sternDraft, "vs-lg", "vs-lg-12");
        this.changeSize(tiltCell, "vs-lg", "vs-lg-5");
        this.changeSize(trim, "vs-lg", "vs-lg-4");
        this.changeSize(trimCell, "vs-lg", "vs-lg-6");
        this.changeSize(heel, "vs-lg", "vs-lg-4");
        this.changeSize(heelCell, "vs-lg", "vs-lg-5");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "17%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPad" && deviceInfo.deviceOrientation === "portrait" && deviceInfo.width <= 980 ) {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "start";
        draftCell.style.alignItems = "start";
        tiltCell.style.alignItems = "start";
        dateCell.style.alignItems = "start";

        sternDraft.style.marginTop = "5%";
        trimCell.style.left = "-8%";
        trimImage.style.left = "";
        referenceTrimLine.style.width = "85%";
        referenceTrimLine.style.left = "";
        heel.style.marginTop = "5%";
        heelCell.style.marginTop = "5%";
        heelCell.style.left = "-11%";
        heelText.style.marginLeft = "";
        heelImage.style.left = "16%";
        referenceHeelLine.style.width = "70%";
        referenceHeelLine.style.left = "";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-sm", "vs-sm-2");
        this.changeSize(draftCell, "vs-sm", "vs-sm-2");
        this.changeSize(bowDraft, "vs-sm", "vs-sm-12");
        this.changeSize(sternDraft, "vs-sm", "vs-sm-12");
        this.changeSize(tiltCell, "vs-sm", "vs-sm-5");
        this.changeSize(tb5Cell, "vs-sm", "vs-sm-3");
        this.changeSize(tb6Cell, "vs-sm", "vs-sm-3");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "17%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPhone" && deviceInfo.deviceOrientation === "landscape" && 667 < deviceInfo.width && deviceInfo.width <= 812 ) {

        draftCell.style.order = "3";
        tiltCell.style.order = "4";
        dateCell.style.order = "2";

        nameCell.style.alignItems = "center";
        draftCell.style.alignItems = "center";
        tiltCell.style.alignItems = "center";
        dateCell.style.alignItems = "center";

        heelText.style.display = "flex";
        heelText.style.alignItems = "center";

        tiltCell.style.marginLeft = "";
        sternDraft.style.marginLeft = "-5%";
        bowDraftValue.style.marginBottom = "2%";
        sternDraftValue.style.marginBottom = "2%";
        tirmValue.style.marginBottom = "2%";
        heelValue.style.marginBottom = "2%";

        trimCell.style.display = "none";
        heelCell.style.display = "none";

        this.changeSize(nameValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(bowDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(sternDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(tirmValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(heelValue, "aisea-text-", "aisea-text-20-400-black");

        this.changeSize(nameCell, "vs-sm", "vs-sm-9");
        this.changeSize(draftCell, "vs-sm", "vs-sm-6");
        this.changeSize(bowDraft, "vs-sm", "vs-sm-6");
        this.changeSize(sternDraft, "vs-sm", "vs-sm-6");
        this.changeSize(tiltCell, "vs-sm", "vs-sm-6");
        this.changeSize(communicationErrorCell, "vs-sm", "vs-sm-6");
        this.changeSize(tb5Cell, "vs-sm", "vs-sm-2");
        this.changeSize(tb6Cell, "vs-sm", "vs-sm-2");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "25%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPhone" && deviceInfo.deviceOrientation === "landscape" && deviceInfo.width <= 667 ) {

        draftCell.style.order = "3";
        tiltCell.style.order = "4";
        dateCell.style.order = "2";

        nameCell.style.alignItems = "center";
        draftCell.style.alignItems = "center";
        tiltCell.style.alignItems = "center";
        dateCell.style.alignItems = "center";

        tiltCell.style.marginLeft = "";
        sternDraft.style.marginLeft = "-5%";
        bowDraftValue.style.marginBottom = "3%";
        sternDraftValue.style.marginBottom = "3%";
        tirmValue.style.marginBottom = "3%";
        heelValue.style.marginBottom = "3%";

        trimCell.style.display = "none";
        heelCell.style.display = "none";

        this.changeSize(nameValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(bowDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(sternDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(tirmValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(heelValue, "aisea-text-", "aisea-text-20-400-black");

        this.changeSize(nameCell, "vs-sm", "vs-sm-9");
        this.changeSize(draftCell, "vs-sm", "vs-sm-6");
        this.changeSize(bowDraft, "vs-sm", "vs-sm-6");
        this.changeSize(sternDraft, "vs-sm", "vs-sm-6");
        this.changeSize(tiltCell, "vs-sm", "vs-sm-6");
        this.changeSize(communicationErrorCell, "vs-sm", "vs-sm-6");
        this.changeSize(tb5Cell, "vs-sm", "vs-sm-2");
        this.changeSize(tb6Cell, "vs-sm", "vs-sm-2");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "25%";
        }

        tanks.style.width = "100%";

      } else if ( deviceInfo.deviceType === "iPhone" && deviceInfo.deviceOrientation === "portrait" ) {

        draftCell.style.order = "3";
        tiltCell.style.order = "4";
        dateCell.style.order = "2";

        nameCell.style.alignItems = "center";
        draftCell.style.alignItems = "center";
        tiltCell.style.alignItems = "center";
        dateCell.style.alignItems = "center";

        tiltCell.style.marginLeft = "8%";
        sternDraft.style.marginLeft = "";
        bowDraftValue.style.marginBottom = "1%";
        sternDraftValue.style.marginBottom = "1%";
        tirmValue.style.marginBottom = "1%";
        heelValue.style.marginBottom = "1%";

        trimCell.style.display = "none";
        heelCell.style.display = "none";

        this.changeSize(nameValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(bowDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(sternDraftValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(tirmValue, "aisea-text-", "aisea-text-20-400-black");
        this.changeSize(heelValue, "aisea-text-", "aisea-text-20-400-black");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "19%";
        }

        tanks.style.width = "100%";

      } else {

        draftCell.style.order = "2";
        tiltCell.style.order = "3";
        dateCell.style.order = "4";

        nameCell.style.alignItems = "center";
        draftCell.style.alignItems = "center";
        tiltCell.style.alignItems = "center";
        dateCell.style.alignItems = "center";

        sternDraft.style.marginTop = "";
        trimCell.style.left = "-6%";
        trimImage.style.left = "";

        heel.style.marginTop = "";
        heelCell.style.marginTop = "";
        heelCell.style.left = "-3%";
        heelText.style.marginLeft = "-10%";
        heelImage.style.left = "19%";

        referenceTrimLine.style.width = "90%";
        referenceHeelLine.style.width = "90%";

        trimCell.style.display = "flex";
        heelCell.style.display = "flex";

        this.changeSize(nameCell, "vs-lg", "vs-lg-2");
        this.changeSize(draftCell, "vs-lg", "vs-lg-3");
        this.changeSize(bowDraft, "vs-lg", "vs-lg-6");
        this.changeSize(sternDraft, "vs-lg", "vs-lg-6");
        this.changeSize(tiltCell, "vs-lg", "vs-lg-5");
        this.changeSize(trim, "vs-lg", "vs-lg-4");
        this.changeSize(trimCell, "vs-lg", "vs-lg-3");
        this.changeSize(heel, "vs-lg", "vs-lg-3");
        this.changeSize(heelCell, "vs-lg", "vs-lg-2");

        for (let i = 0; i < accordionButton.length; i++) {
          accordionButton[i].style.top = "50%";
        }

      }
    },
    changeSize(cellData, checkValue, chengeValue) {
      cellData.classList.forEach(d => {
        if (d.indexOf(checkValue) != -1) {
          let sm = d;
          cellData.classList.remove(sm);
          cellData.classList.add(chengeValue);
        }
      });
    },
  },
  beforeDestroy() {
    this.unsubscribeFirestore();
  },
  mounted() {
    this.setContentSize();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
#liquid-level-vessel-info {
  padding-bottom: 20px;
  border-bottom: solid 1px $aisea-light-gray;

  .vs-collapse,
  .vs-collapse-item--header {
    padding: initial;
  }
  .con-content--item {
    padding: 0.5rem;
  }
  .tanks {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    overflow-x: hidden;
  }
  .tanks::-webkit-scrollbar {
    height: 8px;
  }
  .tanks::-webkit-scrollbar-thumb {
    background-color: $aisea-light-gray;
    border-radius: 5px;
  }
  .open-item {
    .tanks {
      overflow-x: scroll;
    }
  }
  .fuel-oil,
  .fresh-water,
  .ballast,
  .lost {
    margin: 8px;
  }
  .fuel-oil {
    flex: 0 0 150px;
  }
  .fresh-water {
    flex: 0 0 80px;
  }
  .ballast {
    flex: 0 0 650px;
  }
  .lost {
    flex: 0 0 100px;
  }
  .content-row {
    height: fit-content;
    display: flex;
    font-size: 12px;
    margin-top: 8px;
  }
  .draft-cell,
  .tilt-cell {
    display: flex;
    flex-wrap: wrap;
  }
  .trim-image-area,
  .heel-image-area {
    position: relative;
    min-height: 25px;
    max-height: 25px;
  }
  .trim-image {
    position: absolute;
    width: 90%;
    height: 100%;
    top: -20%;
    left: 5%;
  }
  .heel-image {
    position: absolute;
    z-index: 1;
    width: 70%;
    height: 100%;
    left: 19%;
  }
  .reference-trim-line,
  .reference-trim-line::after {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    content: "";
    display: inline-block;
    width: 90%;
    height: 20px;
    border-top: 1px solid $aisea-blue;
    transform: translateX(-50%);
  }
  .reference-trim-line::after {
    top: -11px;
    left: 35%;
    width: 7px;
    transform: rotate(90deg);
  }
  .reference-heel-line,
  .reference-heel-line::after {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    content: "";
    display: inline-block;
    width: 90%;
    height: 20px;
    border-top: 1px solid $aisea-blue;
    transform: translateX(-50%);
  }
  .reference-heel-line::after {
    top: -11px;
    left: 35%;
    width: 7px;
    transform: rotate(90deg);
  }
  .data-reception {
    font-size: 8px;
  }
  .circle-display {
    width: 17px;
    height: 17px;
    border: 2px solid;
    border-radius: 50%;
    margin-top: 1px;
    margin-right: 8%;
  }
}
</style>
